/**
 * In-archive core journey progress — warm checklist inside a family archive.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Check, ChevronRight, ImageIcon, Mic, UserPlus, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import {
  CORE_EMOTIONAL_LINES,
  CORE_JOURNEY_STEPS,
  CORE_PRODUCT_PROMISE,
} from '../../content/coreJourneyContent';
import { deriveCoreJourneyStepStatus, useTreeCoreProgress } from '../../hooks/useTreeCoreProgress';
import { buildTreePeopleAddPersonUrl } from '../../utils/archiveNavigation';
import { personHref } from '../../utils/prettyArchiveLinks';
import { legacyBookPreviewHrefFromId } from '../../utils/legacyBookPreview';

const STEP_ICONS = {
  first_person: Users,
  second_person: UserPlus,
  first_memory: Mic,
  first_photo: ImageIcon,
  preview_book: BookOpen,
};

function StepRow({ step, done, isNext, action }) {
  const Icon = STEP_ICONS[step.id] || Users;
  return (
    <li
      className={cn(
        'flex gap-3 rounded-xl border px-4 py-3.5 transition',
        isNext && !done && 'border-[#c9a961]/35 bg-[#fffaf3]/90 ring-1 ring-[#c9a961]/15',
        done && 'border-[hsl(88_18%_78%)]/70 bg-[hsl(88_24%_96%)]/50',
        !done && !isNext && 'border-[hsl(38_14%_88%)]/80 bg-white/50',
      )}
      data-testid={`core-journey-step-${step.id}`}
    >
      <span
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border',
          done ? 'border-[hsl(88_18%_72%)] bg-white text-[hsl(88_18%_32%)]' : 'border-[hsl(38_14%_88%)] bg-[hsl(42_28%_99%)] text-[#8b7355]',
        )}
      >
        {done ? <Check className="h-4 w-4" aria-hidden /> : <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-sm text-[hsl(24_12%_18%)]">{step.title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{step.hint}</p>
        {action && isNext && !done ? <div className="mt-2.5">{action}</div> : null}
      </div>
    </li>
  );
}

export function CoreArchiveJourneyPanel({ treeId, treeLinkBase, className }) {
  const { progress, loading } = useTreeCoreProgress(treeId);
  const status = useMemo(() => deriveCoreJourneyStepStatus(progress), [progress]);

  const completed = CORE_JOURNEY_STEPS.filter((s) => status[s.id]).length;
  const percent = Math.round((completed / CORE_JOURNEY_STEPS.length) * 100);
  const nextStepId = CORE_JOURNEY_STEPS.find((s) => !status[s.id])?.id;

  const peopleHref = buildTreePeopleAddPersonUrl(treeId);
  const memoriesHref = progress?.firstPersonId
    ? `${personHref({ id: progress.firstPersonId })}?view=stories`
    : peopleHref;
  const photosHref = progress?.firstPersonId
    ? `${personHref({ id: progress.firstPersonId })}?view=documents`
    : peopleHref;
  const bookHref = progress?.firstPersonId
    ? legacyBookPreviewHrefFromId(progress.firstPersonId)
    : `${treeLinkBase || `/tree/${treeId}`}?tab=readiness`;

  const actionFor = (id) => {
    const btn = (label, to, testId) => (
      <Button asChild size="sm" className="rounded-full" data-testid={testId}>
        <Link to={to}>
          {label}
          <ChevronRight className="ml-1 h-3.5 w-3.5" aria-hidden />
        </Link>
      </Button>
    );
    switch (id) {
      case 'first_person':
      case 'second_person':
        return btn(id === 'first_person' ? 'Add a loved one' : 'Add another person', peopleHref, `core-journey-cta-${id}`);
      case 'first_memory':
        return btn('Save a memory', memoriesHref, 'core-journey-cta-memory');
      case 'first_photo':
        return btn('Add a photo', photosHref, 'core-journey-cta-photo');
      case 'preview_book':
        return btn('Preview Legacy Book', bookHref, 'core-journey-cta-book');
      default:
        return null;
    }
  };

  return (
    <section
      className={cn(
        'mb-6 overflow-hidden rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-gradient-to-br from-[hsl(42_30%_100%)] to-[hsl(40_22%_97%)] p-5 shadow-[0_8px_32px_-20px_hsl(28_14%_18%_/_0.1)] sm:p-6',
        className,
      )}
      data-testid="core-archive-journey-panel"
      aria-label="Your archive journey"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
            {CORE_EMOTIONAL_LINES.preserveBeforeFade}
          </p>
          <h2 className="font-serif text-xl font-light text-[#1c1917] sm:text-2xl">Your path to a Legacy Book</h2>
          <p className="max-w-xl text-sm leading-relaxed text-[#5c534a]">{CORE_PRODUCT_PROMISE}</p>
          <p className="text-xs text-[#7a7268]">{CORE_EMOTIONAL_LINES.bookNotPerfect}</p>
        </div>
        <div className="flex shrink-0 flex-col items-center rounded-2xl border border-[hsl(38_14%_88%)] bg-white/70 px-5 py-4 text-center">
          <p className="font-serif text-2xl tabular-nums text-[#1c1917]">{loading ? '…' : `${percent}%`}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {loading ? 'Checking progress…' : `${completed} of ${CORE_JOURNEY_STEPS.length} steps`}
          </p>
        </div>
      </div>

      <ul className="mt-5 space-y-2">
        {CORE_JOURNEY_STEPS.map((step) => (
          <StepRow
            key={step.id}
            step={step}
            done={status[step.id]}
            isNext={step.id === nextStepId}
            action={actionFor(step.id)}
          />
        ))}
      </ul>
    </section>
  );
}

export default CoreArchiveJourneyPanel;
